/**
 * Stone Investment Coming Soon Page
 * 
 * Coming soon / under construction page with Stone Investment's luxury aesthetic:
 * - Cinematic full-screen layout
 * - Newsletter signup option
 * - Countdown timer option
 */

'use client';

import { useState, useEffect } from 'react';
import { Mail, ArrowRight } from 'lucide-react';
import Link from 'next/link';

export interface StoneComingSoonProps {
  /**
   * Page title
   */
  title?: string;
  /**
   * Subtitle/description
   */
  subtitle?: string;
  /**
   * Launch date for countdown (ISO string)
   */
  launchDate?: string;
  /**
   * Show newsletter signup
   */
  showNewsletter?: boolean;
  /**
   * Newsletter submit handler
   */
  onNewsletterSubmit?: (email: string) => Promise<void>;
  /**
   * Background image URL
   */
  backgroundImage?: string;
  /**
   * Home link URL
   */
  homeLink?: string;
  /**
   * Contact link URL
   */
  contactLink?: string;
  /**
   * Translation messages (can be flat or nested stone.comingSoon structure)
   */
  messages?: Record<string, unknown>;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * Calculate time remaining until a date
 */
function getTimeRemaining(targetDate: string) {
  const now = new Date().getTime();
  const target = new Date(targetDate).getTime();
  const distance = target - now;
  
  if (distance < 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  
  return {
    days: Math.floor(distance / (1000 * 60 * 60 * 24)),
    hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((distance % (1000 * 60)) / 1000),
  };
}

/**
 * StoneComingSoon - Coming soon page
 */
export function StoneComingSoon({
  title,
  subtitle,
  launchDate,
  showNewsletter = true,
  onNewsletterSubmit,
  backgroundImage,
  homeLink = '/',
  contactLink,
  messages = {},
  className = '',
}: StoneComingSoonProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(
    launchDate ? getTimeRemaining(launchDate) : null
  );
  
  // Get stone-specific translations (support both flat and nested structures)
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const comingSoonMessages = (stoneMessages.comingSoon || messages) as Record<string, string>;
  const commonMessages = (stoneMessages.common || {}) as Record<string, string>;
  
  const t = (key: string, fallback: string): string => {
    return comingSoonMessages[key] || fallback;
  };
  
  // Resolved title and subtitle
  const resolvedTitle = title || t('title', 'Coming Soon');
  const resolvedSubtitle = subtitle || t('subtitle', 'We are working on something exceptional. Stay tuned.');
  
  // Countdown timer
  useEffect(() => {
    if (!launchDate) return;
    
    const interval = setInterval(() => {
      setTimeRemaining(getTimeRemaining(launchDate));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [launchDate]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !onNewsletterSubmit) return;
    
    setIsSubmitting(true);
    try {
      await onNewsletterSubmit(email);
      setIsSubmitted(true);
      setEmail('');
    } catch (error) {
      console.error('Newsletter signup failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div 
      className={`min-h-screen flex flex-col items-center justify-center relative overflow-hidden ${className}`}
    >
      {/* Background */}
      {backgroundImage ? (
        <>
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
          <div className="absolute inset-0 bg-stone-900/70" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900" />
      )}
      
      {/* Grain Texture */}
      <div 
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '200px 200px'
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 text-center px-6 md:px-12 max-w-3xl">
        {/* Logo/Brand Placeholder */}
        <div className="w-16 h-16 border border-stone-600 mx-auto mb-12 flex items-center justify-center">
          <span className="font-serif text-2xl text-white">S</span>
        </div>
        
        {/* Title */}
        <h1 
          className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-6"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          {resolvedTitle}
        </h1>
        
        {/* Subtitle */}
        <p className="text-lg text-stone-300 font-sans mb-12 max-w-lg mx-auto">
          {resolvedSubtitle}
        </p>
        
        {/* Countdown */}
        {timeRemaining && (
          <div className="flex justify-center gap-8 mb-16">
            {[
              { value: timeRemaining.days, label: t('days', 'Days') },
              { value: timeRemaining.hours, label: t('hours', 'Hours') },
              { value: timeRemaining.minutes, label: t('minutes', 'Minutes') },
              { value: timeRemaining.seconds, label: t('seconds', 'Seconds') },
            ].map(({ value, label }) => (
              <div key={label} className="text-center">
                <span className="font-serif text-4xl md:text-5xl text-white block mb-2">
                  {String(value).padStart(2, '0')}
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-sans">
                  {label}
                </span>
              </div>
            ))}
          </div>
        )}
        
        {/* Newsletter Signup */}
        {showNewsletter && onNewsletterSubmit && (
          <div className="max-w-md mx-auto mb-12">
            {isSubmitted ? (
              <p className="text-stone-300 font-sans">
                {t('thankYou', 'Thank you! We will notify you when we launch.')}
              </p>
            ) : (
              <form onSubmit={handleSubmit} className="flex gap-2">
                <div className="flex-1 relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('enterEmail', 'Enter your email')}
                    required
                    className="w-full h-12 pl-12 pr-4 bg-white/10 backdrop-blur-sm border border-stone-600 text-white placeholder:text-stone-500 font-sans focus:outline-none focus:border-stone-400 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-12 px-6 bg-white text-stone-900 font-sans text-sm uppercase tracking-[0.1em] hover:bg-stone-100 disabled:bg-stone-400 transition-colors flex items-center gap-2"
                >
                  <span>{commonMessages.notifyMe || t('notifyMe', 'Notify Me')}</span>
                  <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        )}
        
        {/* Links */}
        <div className="flex justify-center gap-8">
          <Link
            href={homeLink}
            className="text-sm font-sans text-stone-400 hover:text-white transition-colors"
          >
            {commonMessages.backToHome || t('backToHome', 'Back to Home')}
          </Link>
          {contactLink && (
            <Link
              href={contactLink}
              className="text-sm font-sans text-stone-400 hover:text-white transition-colors"
            >
              {commonMessages.contactUs || t('contactUs', 'Contact Us')}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default StoneComingSoon;
